import React, { useCallback, useMemo } from 'react';

import { Checkbox, message, Tooltip } from 'antd';
import { OverrideContainer, OverrideTabContainer } from './styles';
import { DebounceInput } from '../../../components/DebounceInput';
import { deepCopy } from '../../../utils/javascript';
import { postDataSetOverride } from '../../../utils/services/complexDocumentsAPI';
import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../../context/complexDocumentContext';
import { useTranslation } from 'react-i18next';
import { COMPLEX_VARIABLES_OVERRIDE_SIDEBAR } from './translationValues';
import { VARIABLE_PRIMITIVE_TYPES, VARIABLE_TYPE } from '../../../utils/constants/appConstants';
import { CONTEXT_KEYS, useArtboardContext } from '../../../context/complexArtboardContext';
import { COMPLEX_DOCUMENT_VERSION } from '../../../utils/complexDocumentConstants';
import { postEmailDataSetOverride } from '../../../utils/services/complexEmailAPI';
import { useVersionContext, VERSION_CONTEXT_KEYS } from '../../../context/complexVersionContext';

const validVariableTypes = [VARIABLE_TYPE.STRING_TITLE_CASE, VARIABLE_TYPE.OBJECT];
const NAMESPACES = ['COMPLEX_VARIABLES_OVERRIDE_SIDEBAR'];

export const ComplexVariableOverrideList = ({
  variablesList = [],
  editable,
  attachmentOverrides = [],
  isEmailTemplate = false,
}) => {
  const { t } = useTranslation(NAMESPACES);

  const variablesSelector = useCallback(store => {
    return {
      documentId: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID],
      overrideList: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.VARIABLE_OVERRIDES],
    };
  }, []);
  const [{ documentId, overrideList = [] }, methods] = useDocumentContext(variablesSelector);
  const lifecycleSelector = useCallback(store => store[VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE], []);
  const [lifeCycle, updateVersionStore] = useVersionContext(lifecycleSelector, `ComplexVariableOverrideList`);

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `ComplexVariableOverrideList`);

  const setOverrideList = useCallback(list => {
    methods.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.VARIABLE_OVERRIDES]: list });
  }, []);

  const [newList, originalList] = useMemo(() => {
    // get all valid variables of type string, date and list string. within nested objects as well.
    let filterList = deepCopy(variablesList)
      .flatMap(firstLevelVariable => {
        const firstLevelName = firstLevelVariable.name;
        if (VARIABLE_TYPE.OBJECT === firstLevelVariable.type) {
          return firstLevelVariable.variables.flatMap(secondLevelVariable => {
            if (secondLevelVariable.type === VARIABLE_TYPE.CHART_OBJECT) {
              // return secondLevelVariable.variables;
              return [];
            }
            return { ...secondLevelVariable, objectName: firstLevelName, type: 'object' };
          });
        }
        return firstLevelVariable;
      })
      .filter(
        firstLevelVariable =>
          [...VARIABLE_PRIMITIVE_TYPES, VARIABLE_TYPE.OBJECT, VARIABLE_TYPE.HTML].includes(firstLevelVariable.type) &&
          firstLevelVariable.id,
      );
    const originalList = deepCopy(filterList);

    let newObjectName = '';
    // construct an list based on whether an list has different values from the override values from the override list.
    filterList = filterList.map(firstLevelVariable => {
      const foundOverrideVariable = overrideList.find(
        overrideVariable =>
          overrideVariable.id === firstLevelVariable.id &&
          (!firstLevelVariable.objectName
            ? overrideVariable.name === firstLevelVariable.placeHolderText
            : overrideVariable.name === firstLevelVariable.objectName),
      );
      let isObject = false;
      if (firstLevelVariable.objectName && firstLevelVariable.objectName !== newObjectName) {
        newObjectName = firstLevelVariable.objectName;
        isObject = true;
      } else if (!firstLevelVariable.objectName) {
        newObjectName = '';
      }
      const value = {
        id: firstLevelVariable.id,
        name: firstLevelVariable.placeHolderText,
        length: firstLevelVariable.length,
        mandatory: firstLevelVariable.mandatory,
        checkLength: firstLevelVariable.checkLength,
        type: firstLevelVariable.type,
        objectName: firstLevelVariable.objectName,
        isObject,
      };
      if (foundOverrideVariable) {
        foundOverrideVariable.overrides.forEach(overridenKeys => {
          if (overridenKeys.key === 'length' && firstLevelVariable.length !== overridenKeys.value) {
            value.length = overridenKeys.value;
            value.overridden = true;
          }
          if (
            overridenKeys.key === 'checkLength' &&
            firstLevelVariable.checkLength !== overridenKeys.value
          ) {
            value.checkLength = overridenKeys.value;
            value.overridden = true;
          }
          if (
            overridenKeys.key === 'mandatory' &&
            firstLevelVariable.mandatory !== overridenKeys.value
          ) {
            value.mandatory = overridenKeys.value;
            value.overridden = true;
          }
        });
      }
      const attachmentOverrideVariable = attachmentOverrides.find(
        overrideVariable => overrideVariable.id === firstLevelVariable.id,
      );
      if (attachmentOverrideVariable) {
        value.attachment = true;
      }
      return value;
    });
    return [filterList, originalList];
  }, [variablesList, overrideList]);

  const updateLifecycleTimeline = () => {
    updateVersionStore({
      [VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE]: {
        ...lifeCycle,
        [COMPLEX_DOCUMENT_VERSION.DRAFT]: new Date(),
      },
    });
  };

  const handleOverrideLength = (checked, overrides, originalVariable) => {
    if (checked) {
      overrides.push({ key: 'length', value: originalVariable.length || 20 });
    } else {
      overrides = overrides.filter(v => v.key !== 'length');
    }
    return overrides;
  };

  const onOverrideChange = useCallback(
    async (key, value, variable) => {
      const overrideVariableIndex = overrideList.findIndex(
        overrideVariable =>
          overrideVariable.id === variable.id &&
          (!variable.objectName
            ? overrideVariable.name === variable.name
            : overrideVariable.name === variable.objectName),
      );
      let overrideListCopy = deepCopy(overrideList);
      const originalVariable = originalList.find(gVar => gVar.id === variable.id);
      // check if override list already has the variable
      if (overrideVariableIndex !== -1) {
        const firstLevelVariable = overrideListCopy[overrideVariableIndex];
        const localOverrideExists = firstLevelVariable.overrides.find(
          overrideVariable => key === overrideVariable.key,
        );
        // check if the overriden variable has the particular overriden key. if not, create a new override key and values.
        if (localOverrideExists) {
          firstLevelVariable.overrides = firstLevelVariable.overrides.map(overrideVariable => {
            if (key === overrideVariable.key) {
              overrideVariable.value = value;
            }
            return overrideVariable;
          });
        } else {
          firstLevelVariable.overrides.push({ key, value: value });
        }
        // add or remove length value in overrides based on Whether checkLength is true or false
        if (key === 'checkLength') {
          firstLevelVariable.overrides = handleOverrideLength(
            value,
            firstLevelVariable.overrides,
            originalVariable,
          );
        }
        // add checkLength value in overrides based on whether length is modified.
        if (
          key === 'length' &&
          !firstLevelVariable.overrides.find(value => value.key === 'checkLength')
        ) {
          firstLevelVariable.overrides = [
            ...firstLevelVariable.overrides,
            { key: 'checkLength', value: true },
          ];
        }
        const isSameAsGlobal = firstLevelVariable.overrides.every(overrideVariable => {
          if (
            'mandatory' === overrideVariable.key &&
            overrideVariable.value === originalVariable.mandatory
          ) {
            return true;
          } else if (
            'length' === overrideVariable.key &&
            overrideVariable.value === originalVariable.length
          ) {
            return true;
          } else if (
            'checkLength' === overrideVariable.key &&
            overrideVariable.value === originalVariable.checkLength
          ) {
            return true;
          }
          return false;
        });
        // if the overriden values are same as global values, then remove the override entry from the list.
        if (isSameAsGlobal) {
          overrideListCopy = overrideListCopy.filter(
            (val, index) => index !== overrideVariableIndex,
          );
        }
      } else {
        const listItem = {
          id: variable.id,
          name: variable.objectName || variable.name,
          type: variable.type === 'ListString' ? 'listOfStrings' : variable.type.toLowerCase(),
          overrides: [],
        };
        listItem.overrides.push({ key, value: value });
        // add or remove length value in overrides based on Whether checkLength is true or false
        if (key === 'checkLength') {
          listItem.overrides = handleOverrideLength(value, listItem.overrides, originalVariable);
        }
        // add checkLength value in overrides based on whether length is modified.
        if (
          key === 'length' &&
          !listItem.overrides.find(value => value.key === 'checkLength')
        ) {
          listItem.overrides = [
            ...listItem.overrides,
            { key: 'checkLength', value: true },
          ];
        }
        overrideListCopy.push(listItem);
      }
      let postDataSetOverrideApi = postDataSetOverride;
      if (isEmailTemplate) {
        postDataSetOverrideApi = postEmailDataSetOverride;
      }

      try {
        const response = await postDataSetOverrideApi(documentId, overrideListCopy);
        if (response) {
          setOverrideList(overrideListCopy);
          updateLifecycleTimeline();
        }
      } catch (e) {
        console.log(e);
        message.error(
          t(
            COMPLEX_VARIABLES_OVERRIDE_SIDEBAR.ADD_UPDATE_OVERRIDE_VARIABLE_FAILURE.key,
            COMPLEX_VARIABLES_OVERRIDE_SIDEBAR.ADD_UPDATE_OVERRIDE_VARIABLE_FAILURE.value,
          ),
        );
      }
    },
    [documentId, overrideList, originalList, t, setOverrideList],
  );

  const onReset = async variable => {
    let overrideListCopy = [];
    if (variable) {
      overrideListCopy = deepCopy(overrideList);
      overrideListCopy = overrideListCopy.filter(
        overrideVariable => overrideVariable.id !== variable.id,
      );
    }

    let postDataSetOverrideApi = postDataSetOverride;
    if (isEmailTemplate) {
      postDataSetOverrideApi = postEmailDataSetOverride;
    }

    try {
      const response = await postDataSetOverrideApi(documentId, overrideListCopy);
      if (response) {
        setOverrideList(overrideListCopy);
        updateLifecycleTimeline();
      }
    } catch (e) {
      console.log(e);
      message.error(
        t(
          COMPLEX_VARIABLES_OVERRIDE_SIDEBAR.ADD_UPDATE_OVERRIDE_VARIABLE_FAILURE.key,
          COMPLEX_VARIABLES_OVERRIDE_SIDEBAR.ADD_UPDATE_OVERRIDE_VARIABLE_FAILURE.value,
        ),
      );
    }
  };

  return (
    <OverrideTabContainer editable={editable} isInteractiveDesign={isInteractiveDesign}>
      {newList?.length ? (
        <a className="reset-all" onClick={() => onReset()}>
          {t(
            COMPLEX_VARIABLES_OVERRIDE_SIDEBAR.RESET_ALL_LABEL.key,
            COMPLEX_VARIABLES_OVERRIDE_SIDEBAR.RESET_ALL_LABEL.value,
          )}
        </a>
      ) : null}
      {newList.map(variable => {
        return (
          <OverrideContainer isObject={!!variable.objectName} editable={editable} isInteractiveDesign={isInteractiveDesign}>
            {variable.isObject ? (
              <div className="object-container">{variable.objectName}</div>
            ) : null}
            <div className="name-container">
              <div className="variable-name">{variable.name}</div>
              {variable.attachment ? (
                <Tooltip
                  title={t(
                    COMPLEX_VARIABLES_OVERRIDE_SIDEBAR.ATTACHMENT_USED_VARIABLE_LABEL.key,
                    COMPLEX_VARIABLES_OVERRIDE_SIDEBAR.ATTACHMENT_USED_VARIABLE_LABEL.value,
                  )}
                >
                  <div className="variable-type yellow-bg"></div>
                </Tooltip>
              ) : null}
              {variable.overridden ? (
                <Tooltip
                  title={t(
                    COMPLEX_VARIABLES_OVERRIDE_SIDEBAR.OVERRIDDEN_LABEL.key,
                    COMPLEX_VARIABLES_OVERRIDE_SIDEBAR.OVERRIDDEN_LABEL.value,
                  )}
                >
                  <div className="variable-type"></div>
                </Tooltip>
              ) : null}
            </div>
            <div className="override-container">
              <div className="required-container">
                {variable.mandatory != null ? (
                  <div className="required-label">
                    <label>
                      {`${t(
                        COMPLEX_VARIABLES_OVERRIDE_SIDEBAR.REQUIRED_LABEL.key,
                        COMPLEX_VARIABLES_OVERRIDE_SIDEBAR.REQUIRED_LABEL.value,
                      )} : `}
                    </label>
                    <Checkbox
                      checked={variable.mandatory}
                      onChange={e => onOverrideChange('mandatory', e.target.checked, variable)}
                    />
                  </div>
                ) : null}
                {validVariableTypes.includes(variable.type) ? (
                  <div className="required-label">
                    <label>{`${t(
                      COMPLEX_VARIABLES_OVERRIDE_SIDEBAR.LENGTH_LABEL.key,
                      COMPLEX_VARIABLES_OVERRIDE_SIDEBAR.LENGTH_LABEL.value,
                    )} : `}</label>
                    <Checkbox
                      checked={variable.checkLength}
                      onChange={e => {
                        onOverrideChange('checkLength', e.target.checked, variable);
                      }}
                    />
                  </div>
                ) : null}
                {variable.checkLength && validVariableTypes.includes(variable.type) ? (
                  <div>
                    <label style={{ minWidth: '36%' }}>
                      {`${t(
                        COMPLEX_VARIABLES_OVERRIDE_SIDEBAR.MAX_CHARACTERS_LABEL.key,
                        COMPLEX_VARIABLES_OVERRIDE_SIDEBAR.MAX_CHARACTERS_LABEL.value,
                      )} : `}
                    </label>
                    <DebounceInput
                      value={variable.length}
                      onChange={value => onOverrideChange('length', value, variable)}
                      inputType="number"
                      min={1}
                    />
                  </div>
                ) : null}
              </div>
              <a onClick={() => onReset(variable)} className='reset-link'>
                {t(
                  COMPLEX_VARIABLES_OVERRIDE_SIDEBAR.RESET_LABEL.key,
                  COMPLEX_VARIABLES_OVERRIDE_SIDEBAR.RESET_LABEL.value,
                )}
              </a>
            </div>
          </OverrideContainer>
        );
      })}
    </OverrideTabContainer>
  );
};
